import * as React from 'react';
import { useContext, useEffect, useSyncExternalStore } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  answerCall, declineCall, hangupActiveCall, sendDigits, toggleMute,
} from '@/api/calls/call-ring';
import { ReactNative } from '@/api/app/react-native';
import * as Ringtone from '@/api/calls/ringtone';
import { isOnCall, status as twilioStatus } from '@/api/calls/twilio';
import { ringStopped } from '@/redux/app/actions/calls';
import type { CallsState } from '@/redux/app/reducers/calls';
import { useSetupV3CallsChannel } from '@/hooks/websockets/setup';
import { AccountInfoContext } from '../account/account-info';
import { IncomingCallPrompt } from './IncomingCallPrompt';
import { ActiveCallPanel } from './ActiveCallPanel';
import { useRingTitle } from './use-ring-title';

// Fixed overlay holding ring prompts and the active-call panel in the middle of
// the window, where the legacy prompt and dialer open. pointer-events pass
// through it so only the cards are interactive; wheeling over a card still
// scrolls it when the stack outgrows the window.
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  display: flex;
  overflow-y: auto;
  padding: 8px;
  pointer-events: none;
`;

// margin: auto centers the stack but, unlike justify-content, lets it overflow
// downward (scrollable) rather than off the top of the window
const Stack = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const subscribeToTwilioStatus = (onChange: () => void) => {
  twilioStatus.addListener(onChange);
  return () => twilioStatus.removeListener(onChange);
};

// Tracks calls placed or accepted outside this component (legacy dialer and
// incoming prompt), so ring prompts can step aside while the agent is busy
const useOnCall = () => useSyncExternalStore(subscribeToTwilioStatus, isOnCall);

// Mounted by the layout on every authenticated page (see CallManagerApp), so the
// ring prompts and active call follow the user across React and Rails pages.
// Call state lives in the shared Redux store and the page-lifetime CallsChannel,
// both of which outlive this component on Turbolinks navigations.
export const CallManager = () => {
  const dispatch = useDispatch();
  const { subdomain, conferenceRingEnabled } = useContext(AccountInfoContext);
  const calls: CallsState = useSelector((state: any) => state.calls);

  const onCall = useOnCall();

  useSetupV3CallsChannel();

  // Inside the React Native app the phone's own dialer takes the call (see
  // useSetupV3CallsChannel); a web prompt there could only get in its way.
  const enabled = conferenceRingEnabled && !ReactNative.exists() && !!calls;

  // Like a busy Twilio device, an agent on a call is not offered other calls; the
  // rings stay in the store and reappear if the window is still open after hangup.
  // The ring being claimed stays put so its "Connecting…" state remains visible.
  const busy = onCall || !!calls?.active;
  const rings = enabled ? Object.values(calls.incoming).filter((ring) => !busy || calls.claiming === ring.call_id) : [];

  // Ring while a prompt waits for an answer; stop once the agent picks one up
  const ringing = !busy && !calls?.claiming && rings.length > 0;
  useEffect(() => {
    if (!ringing) return undefined;
    Ringtone.start();
    return Ringtone.stop;
  }, [ringing]);
  useRingTitle(ringing ? rings[0] : undefined);

  if (!enabled) return null;
  if (rings.length === 0 && !calls.active) return null;

  return (
    <Overlay>
      <Stack>
        {calls.active && (
          <ActiveCallPanel
            call={calls.active}
            onToggleMute={() => toggleMute(dispatch)}
            onSendDigits={sendDigits}
            onHangup={hangupActiveCall}
          />
        )}
        {rings.map((ring) => (
          <IncomingCallPrompt
            key={ring.call_id}
            ring={ring}
            claiming={calls.claiming === ring.call_id}
            onAnswer={() => answerCall(subdomain, ring, dispatch)}
            onDecline={() => declineCall(subdomain, ring, dispatch)}
            onExpire={() => dispatch(ringStopped({ callId: ring.call_id, reason: 'timeout' }))}
          />
        ))}
      </Stack>
    </Overlay>
  );
};
